import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';

const EditTimeoffModal = ({ open, onClose, onSave, timeoffInstance, relationshipName }) => {
    const [hours, setHours] = useState('');
    const [value, setValue] = useState('0');
    const [comments, setComments] = useState('');

    useEffect(() => {
        if (timeoffInstance) {
            setHours(timeoffInstance.hours || '');
            setValue(timeoffInstance.value || '0');
            setComments(timeoffInstance.comments || '');
        }
    }, [timeoffInstance]);

    const partnerFirstName = relationshipName?.split(' ')[0] || 'Partner';

    const handleSave = () => {
        if (!timeoffInstance) return;
        onSave({
            timeoff_instance_id: timeoffInstance.timeoff_instance_id,
            timeoff_id: timeoffInstance.timeoff_id,
            name: timeoffInstance.name,
            value,
            hours,
            comments,
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
                }
            }}
        >
            <DialogTitle sx={{
                fontWeight: 600,
                color: '#333',
                borderBottom: '1px solid #E8E8E8',
                pb: 2
            }}>
                Edit Time Off: {timeoffInstance?.name}
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', display: 'block', mb: 1.5, fontWeight: 600 }}>
                            Duration (Hours)
                        </Typography>
                        <Select
                            fullWidth
                            value={hours || ''}
                            onChange={(e) => setHours(e.target.value)}
                            displayEmpty
                            sx={{
                                borderRadius: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8E8E8' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7B6B8D' }
                            }}
                        >
                            <MenuItem value="" disabled sx={{ color: '#999' }}>Select Hours</MenuItem>
                            {[...Array(24).keys()].map((num) => (
                                <MenuItem key={`edit-hours-${num + 1}`} value={num + 1}>
                                    {num + 1} {num + 1 === 1 ? 'hour' : 'hours'}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', display: 'block', mb: 1.5, fontWeight: 600 }}>
                            Who took time off?
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                fullWidth
                                variant={value === '0' ? 'contained' : 'outlined'}
                                onClick={() => setValue('0')}
                                sx={{
                                    borderRadius: 2,
                                    py: 1.5,
                                    borderColor: '#7B6B8D',
                                    color: value === '0' ? 'white' : '#7B6B8D',
                                    background: value === '0' ? '#7B6B8D' : 'transparent',
                                    '&:hover': {
                                        borderColor: '#6A5A7A',
                                        background: value === '0' ? '#6A5A7A' : 'rgba(184, 167, 212, 0.08)'
                                    }
                                }}
                            >
                                Me
                            </Button>
                            <Button
                                fullWidth
                                variant={value === '1' ? 'contained' : 'outlined'}
                                onClick={() => setValue('1')}
                                sx={{
                                    borderRadius: 2,
                                    py: 1.5,
                                    borderColor: '#C4856A',
                                    color: value === '1' ? 'white' : '#C4856A',
                                    background: value === '1' ? '#C4856A' : 'transparent',
                                    '&:hover': {
                                        borderColor: '#B0745A',
                                        background: value === '1' ? '#B0745A' : 'rgba(244, 181, 196, 0.08)'
                                    }
                                }}
                            >
                                {partnerFirstName}
                            </Button>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', display: 'block', mb: 1.5, fontWeight: 600 }}>
                            Comments (Optional)
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Add any additional notes..."
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            inputProps={{ maxLength: 200 }}
                            helperText={`${comments.length}/200`}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused fieldset': { borderColor: '#7B6B8D' }
                                }
                            }}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #E8E8E8' }}>
                <Button
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        color: '#666',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!hours || parseInt(hours) <= 0}
                    variant="contained"
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        background: '#7B6B8D',
                        boxShadow: 'none',
                        '&:hover': { background: '#6A5A7A', boxShadow: 'none' },
                        '&:disabled': { background: '#E8E8E8', color: '#999' }
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTimeoffModal;
